import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { exhaustMap, map, withLatestFrom, catchError } from 'rxjs/operators';

import { TrackingPageActionTypes } from '../tracking-page/tracking-page.actions';

import { TrackingPageNumbersDataSerivce } from './tracking-page-numbers-data.service';
import {
  SetCarriers,
  OpenTrackingNumberDetails,
  TrackingPageNumbersTypes,
  AddTrackingPageNumbers,
  SetSidebarItems,
  SetMoreTrackingNumbers
} from './tracking-page-numbers.actions';
import { TrackingPageNumbersFacade } from './tracking-page-numbers.facade';
import { TrackingPageFacade } from '../tracking-page/tracking-page.facade';
import { toPayload } from 'app/helpers';
import { of, empty } from 'rxjs';
import { SetTrackingNumberCheckpoints } from './tracking-page-numbers.actions';

@Injectable()
export class TrackingPageNumbersEffects {
  constructor(
    private actions$: Actions,
    private trackingPageNumbersData: TrackingPageNumbersDataSerivce,
    private trackingPageFacade: TrackingPageFacade,
    private trackingPageNumbersFacade: TrackingPageNumbersFacade
  ) {}

  readonly bootstrap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrackingPageActionTypes.BOOTSTRAP),
      exhaustMap(() =>
        this.trackingPageNumbersData.fetchCarriers().pipe(
          map((items) => {
            TrackingPageNumbersDataSerivce.setCarriers(items);
            return new SetCarriers(items);
          })
        )
      )
    )
  );

  readonly bootstrapSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrackingPageActionTypes.BOOTSTRAP_SUCCESS),
      withLatestFrom(
        this.trackingPageNumbersFacade.firstTrackingNumber$,
        this.trackingPageNumbersFacade.sideBarItems$
      ),
      exhaustMap(
        ([, { id, trackingNumbers = [] } = { id: null }, sidebarItems]) =>
          id && !sidebarItems.length
            ? of(
                new OpenTrackingNumberDetails({
                  id: trackingNumbers[0]?.id,
                  orderId: id
                })
              )
            : empty()
      )
    )
  );

  readonly openTrackingNumberDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrackingPageNumbersTypes.OPEN_TRACKING_NUMBER_DETAILS),
      toPayload(),
      withLatestFrom(this.trackingPageFacade.hash$),
      exhaustMap(([{ id, orderId }, hash]) =>
        this.trackingPageNumbersData.fetchCheckpoints({ id, hash }).pipe(
          map(
            (payload) =>
              new SetTrackingNumberCheckpoints({ ...payload, orderId })
          ),
          catchError(() => empty())
        )
      )
    )
  );

  readonly openSidebarItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrackingPageNumbersTypes.OPEN_SIDEBAR_ITEM),
      toPayload(),
      withLatestFrom(this.trackingPageFacade.hash$),
      exhaustMap(([id, hash]) =>
        this.trackingPageNumbersData.fetchSidebarNumbers(id, hash).pipe(
          map(
            ({ items, totalItems }) =>
              new AddTrackingPageNumbers({ items, totalItems })
          ),
          catchError(() => empty())
        )
      )
    )
  );

  readonly updateReview$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TrackingPageNumbersTypes.UPDATE_REVIEW),
        toPayload(),
        withLatestFrom(this.trackingPageFacade.hash$),
        exhaustMap(([payload, hash]) =>
          this.trackingPageNumbersData
            .updateReview(payload, hash)
            .pipe(catchError(() => empty()))
        )
      ),
    { dispatch: false }
  );

  readonly triggerLoadMoreTrackingNumbers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrackingPageNumbersTypes.LOAD_MORE_TRACKING_NUMBERS),
      withLatestFrom(
        this.trackingPageFacade.hash$,
        this.trackingPageNumbersFacade.sideBarItemsOpened$,
        this.trackingPageNumbersFacade.all$,
        this.trackingPageNumbersFacade.moreTrackingNumberValues$
      ),
      exhaustMap(([, hash, [sidebarId], allItems, trackingNumber]) =>
        this.trackingPageNumbersData
          .loadMoreTrackingNumbers(
            hash,
            sidebarId,
            allItems.length + trackingNumber
          )
          .pipe(
            map((response: any) => new SetMoreTrackingNumbers(response)),
            catchError(() => empty())
          )
      )
    )
  );

  readonly showMore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrackingPageNumbersTypes.SHOW_MORE),
      withLatestFrom(this.trackingPageFacade.hash$),
      exhaustMap(([, hash]) =>
        this.trackingPageNumbersData
          .loadSidebarItems(hash, { itemsPerPage: 999999999 })
          .pipe(
            map((response: any) => new SetSidebarItems(response)),
            catchError(() => empty())
          )
      )
    )
  );

  readonly fetchSidebarItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrackingPageNumbersTypes.FETCH_SIDEBAR_ITEMS),
      toPayload(),
      withLatestFrom(this.trackingPageFacade.hash$),
      exhaustMap(([payload, hash]) =>
        this.trackingPageNumbersData.loadSidebarItems(hash, payload).pipe(
          map((response: any) => new SetSidebarItems(response)),
          catchError(() => empty())
        )
      )
    )
  );
}
